import React, { useState, useEffect } from 'react';
import { walletAdminApi } from '../api';
import toast from 'react-hot-toast';

const CATEGORY_CFG = {
  RECHARGE:       { color: '#22c55e',  bg: 'rgba(34,197,94,0.12)',   icon: '💳', label: 'Recharge'      },
  BATTLE_JOIN:    { color: '#ef4444',  bg: 'rgba(239,68,68,0.12)',   icon: '⚔️', label: 'Battle Join'   },
  BATTLE_CASHBACK:{ color: '#06b6d4',  bg: 'rgba(6,182,212,0.12)',   icon: '↩️', label: 'Cashback'      },
  WALK_REWARD:    { color: '#6366f1',  bg: 'rgba(99,102,241,0.12)',  icon: '🚶', label: 'Walk Reward'   },
  BONUS:          { color: '#f59e0b',  bg: 'rgba(245,158,11,0.12)',  icon: '🎁', label: 'Bonus'         },
  REFUND:         { color: '#8b5cf6',  bg: 'rgba(139,92,246,0.12)',  icon: '🔄', label: 'Refund'        },
  ADMIN_DEDUCT:   { color: '#94a3b8',  bg: 'rgba(148,163,184,0.12)', icon: '🔧', label: 'Admin Deduct'  },
};

const getCfg = (cat) => CATEGORY_CFG[cat] || { color: '#94a3b8', bg: 'rgba(148,163,184,0.12)', icon: '💰', label: cat || 'Other' };

const downloadCSV = (data) => {
  const headers = ['ID','User','Amount','Type','Category','Source','Date'];
  const rows = data.map(r => [
    r.id, r.userName || r.userId, r.amount, r.type, r.category, r.source,
    r.createdAt ? new Date(r.createdAt).toLocaleString('en-IN') : '',
  ]);
  const csv = [headers, ...rows].map(r => r.map(v => `"${v ?? ''}"`).join(',')).join('\n');
  const blob = new Blob([csv], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a'); a.href = url; a.download = 'transactions.csv'; a.click();
  URL.revokeObjectURL(url);
};

const Transactions = () => {
  const [stats, setStats]           = useState(null);
  const [data, setData]             = useState([]);
  const [total, setTotal]           = useState(0);
  const [loading, setLoading]       = useState(true);
  const [page, setPage]             = useState(0);
  const [typeFilter, setTypeFilter] = useState('');
  const [catFilter, setCatFilter]   = useState('');
  const [userIdFilter, setUserIdFilter] = useState('');
  const SIZE = 20;

  useEffect(() => { loadStats(); }, []);
  useEffect(() => { loadData(); }, [page, typeFilter, catFilter]);

  const loadStats = async () => {
    try {
      const res = await walletAdminApi.getStats();
      setStats(res.data?.data);
    } catch { /* silent */ }
  };

  const loadData = async () => {
    setLoading(true);
    try {
      const params = { page, size: SIZE };
      if (typeFilter) params.type     = typeFilter;
      if (catFilter)  params.category = catFilter;
      if (userIdFilter && !isNaN(userIdFilter)) params.userId = userIdFilter;
      const res = await walletAdminApi.getAllTransactions(params);
      setData(res.data?.data || []);
      setTotal(res.data?.total || 0);
    } catch { toast.error('Failed to load transactions'); }
    finally { setLoading(false); }
  };

  const handleSearch = () => { setPage(0); loadData(); };

  const totalPages = Math.ceil(total / SIZE);

  return (
    <div className="animate-in">
      <div className="page-header">
        <div className="page-header-left">
          <div className="breadcrumb"><span>Finance</span> / <span className="current">Transactions</span></div>
          <h1>Wallet Transactions</h1>
          <p style={{ color: 'var(--text-secondary)' }}>All coin movements across every user wallet</p>
        </div>
        <button style={exportBtnS} onClick={() => downloadCSV(data)}>📥 Export CSV</button>
      </div>

      {/* ── Stats ──────────────────────────────────────────────────────────── */}
      {stats && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: '14px', marginBottom: '24px' }}>
          {[
            { label: 'Total Wallets',       value: stats.totalWallets,         color: 'var(--accent-primary)', icon: '👛' },
            { label: 'Coins in Circulation',value: stats.totalBalance?.toLocaleString(), color: '#22c55e', icon: '🪙' },
            { label: 'Total Earned',        value: `+${stats.totalEarned?.toLocaleString()}`, color: '#22c55e', icon: '⬆️' },
            { label: 'Total Spent',         value: `-${stats.totalSpent?.toLocaleString()}`,  color: '#ef4444', icon: '⬇️' },
          ].map((s, i) => (
            <div key={i} style={cardS}>
              <div style={{ fontSize: '1.4rem' }}>{s.icon}</div>
              <div style={{ fontSize: '1.4rem', fontWeight: 800, color: s.color, marginTop: '4px' }}>{s.value}</div>
              <div style={{ fontSize: '0.76rem', color: 'var(--text-muted)' }}>{s.label}</div>
            </div>
          ))}
        </div>
      )}

      {/* ── Second stats row ─────────────────────────────────────────────── */}
      {stats && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: '14px', marginBottom: '24px' }}>
          {[
            { label: 'Total Transactions',   value: stats.totalTransactions,   color: '#6366f1', icon: '📊' },
            { label: 'Recharges',            value: stats.rechargeCount,       color: '#22c55e', icon: '💳' },
            { label: 'Battle Joins',         value: stats.battleJoinCount,     color: '#ef4444', icon: '⚔️' },
          ].map((s, i) => (
            <div key={i} style={{ ...cardS, display: 'flex', alignItems: 'center', gap: '14px', textAlign: 'left' }}>
              <span style={{ fontSize: '2rem' }}>{s.icon}</span>
              <div>
                <div style={{ fontSize: '1.4rem', fontWeight: 800, color: s.color }}>{s.value}</div>
                <div style={{ fontSize: '0.76rem', color: 'var(--text-muted)' }}>{s.label}</div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── Filters ──────────────────────────────────────────────────────── */}
      <div style={{ display: 'flex', gap: '10px', marginBottom: '16px', flexWrap: 'wrap' }}>
        <select style={selS} value={typeFilter} onChange={e => { setTypeFilter(e.target.value); setPage(0); }}>
          <option value="">All Types</option>
          <option value="EARN">EARN</option>
          <option value="SPEND">SPEND</option>
        </select>
        <select style={selS} value={catFilter} onChange={e => { setCatFilter(e.target.value); setPage(0); }}>
          <option value="">All Categories</option>
          {Object.keys(CATEGORY_CFG).map(k => <option key={k} value={k}>{k}</option>)}
        </select>
        <div style={{ display: 'flex', gap: '6px', flex: 1 }}>
          <input style={{ ...selS, flex: 1 }} placeholder="Filter by User ID…"
            value={userIdFilter} onChange={e => setUserIdFilter(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleSearch()} />
          <button style={primaryBtnS} onClick={handleSearch}>🔍 Search</button>
        </div>
        <span style={{ padding: '9px 14px', background: 'var(--bg-tertiary)', border: '1px solid var(--border-color)', borderRadius: '8px', fontSize: '0.82rem', color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
          {total.toLocaleString()} records
        </span>
      </div>

      {/* ── Table ──────────────────────────────────────────────────────────── */}
      <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border-color)', borderRadius: '12px', overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: 'var(--bg-tertiary)', borderBottom: '1px solid var(--border-color)' }}>
                {['#','User','Amount','Type','Category','Source / Description','Date'].map(h => (
                  <th key={h} style={{ padding: '10px 14px', textAlign: 'left', fontSize: '0.73rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={7} style={{ textAlign: 'center', padding: '50px', color: 'var(--text-muted)' }}>Loading…</td></tr>
              ) : data.length === 0 ? (
                <tr><td colSpan={7} style={{ textAlign: 'center', padding: '50px', color: 'var(--text-muted)' }}>No transactions found</td></tr>
              ) : data.map((tx, i) => {
                const cfg = getCfg(tx.category);
                const isEarn = tx.type === 'EARN';
                return (
                  <tr key={tx.id}
                    style={{ borderBottom: '1px solid var(--border-color)', transition: 'background 0.15s' }}
                    onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-hover)'}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                    <td style={{ padding: '10px 14px', fontSize: '0.78rem', color: 'var(--text-muted)' }}>
                      {page * SIZE + i + 1}
                    </td>
                    <td style={{ padding: '10px 14px' }}>
                      <div style={{ fontSize: '0.84rem', fontWeight: 600 }}>{tx.userName || '—'}</div>
                      <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>ID: {tx.userId}</div>
                    </td>
                    <td style={{ padding: '10px 14px' }}>
                      <span style={{ fontWeight: 800, fontSize: '0.95rem', color: isEarn ? '#22c55e' : '#ef4444' }}>
                        {isEarn ? '+' : ''}{tx.amount?.toLocaleString()} 🪙
                      </span>
                    </td>
                    <td style={{ padding: '10px 14px' }}>
                      <span style={{ fontSize: '0.72rem', fontWeight: 700, padding: '3px 8px', borderRadius: '6px',
                        background: isEarn ? 'rgba(34,197,94,0.12)' : 'rgba(239,68,68,0.12)',
                        color: isEarn ? '#22c55e' : '#ef4444' }}>
                        {tx.type}
                      </span>
                    </td>
                    <td style={{ padding: '10px 14px' }}>
                      <span style={{ fontSize: '0.72rem', padding: '3px 8px', borderRadius: '20px',
                        background: cfg.bg, color: cfg.color, fontWeight: 600 }}>
                        {cfg.icon} {cfg.label}
                      </span>
                    </td>
                    <td style={{ padding: '10px 14px', fontSize: '0.82rem', color: 'var(--text-secondary)', maxWidth: '220px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {tx.source || '—'}
                    </td>
                    <td style={{ padding: '10px 14px', fontSize: '0.78rem', color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
                      {tx.createdAt ? new Date(tx.createdAt).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px', borderTop: '1px solid var(--border-color)' }}>
            <span style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>
              {page * SIZE + 1}–{Math.min((page + 1) * SIZE, total)} of {total}
            </span>
            <div style={{ display: 'flex', gap: '6px' }}>
              <button disabled={page === 0} onClick={() => setPage(p => p - 1)}
                style={{ ...actionBtnS, opacity: page === 0 ? 0.4 : 1 }}>‹</button>
              <span style={{ padding: '5px 12px', background: 'var(--accent-primary)', color: 'white', borderRadius: '6px', fontSize: '0.82rem', fontWeight: 600 }}>
                {page + 1} / {totalPages}
              </span>
              <button disabled={page >= totalPages - 1} onClick={() => setPage(p => p + 1)}
                style={{ ...actionBtnS, opacity: page >= totalPages - 1 ? 0.4 : 1 }}>›</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const cardS      = { background: 'var(--bg-card)', border: '1px solid var(--border-color)', borderRadius: '12px', padding: '18px', textAlign: 'center' };
const selS       = { padding: '9px 14px', background: 'var(--bg-tertiary)', border: '1px solid var(--border-color)', borderRadius: '8px', color: 'var(--text-primary)', fontSize: '0.88rem', outline: 'none' };
const primaryBtnS= { padding: '9px 16px', borderRadius: '8px', border: 'none', cursor: 'pointer', fontSize: '0.88rem', fontWeight: 600, background: 'linear-gradient(135deg, #6366f1, #8b5cf6)', color: 'white' };
const exportBtnS = { padding: '9px 18px', borderRadius: '8px', border: 'none', cursor: 'pointer', fontSize: '0.88rem', fontWeight: 600, background: 'rgba(99,102,241,0.15)', color: 'var(--accent-primary)' };
const actionBtnS = { padding: '5px 12px', borderRadius: '6px', border: '1px solid var(--border-color)', cursor: 'pointer', background: 'var(--bg-tertiary)', color: 'var(--text-primary)' };

export default Transactions;
